import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import logo from 'images/pga-hope-logo.svg'
const useStyles = makeStyles(theme => ({
  logo: {
    width: '120px',
    marginBottom: theme.spacing(3),
  },
  list: {
    paddingLeft: theme.spacing(3),
    margin: 0,
    '& > li': {
      marginBottom: theme.spacing(2),
    },
  },
  paragraph: {
    marginBottom: theme.spacing(2),
  },
}))

const ordinalSuffix = day => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th'
  return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'
}

export const formatWaiverEventDate = (startDateTime, timezone) => {
  const date = DateTime.fromISO(startDateTime).setZone(timezone)
  if (!date.isValid) return ''
  return `${date.day}${ordinalSuffix(date.day)} of ${date.toFormat(
    'MMMM yyyy',
  )}`
}

const Waiver = ({ groupEvent }) => {
  const { control, errors } = useFormContext()
  const classes = useStyles()
  const { title, coachFacility, startDateTime, timezone } = groupEvent
  const eventDate = formatWaiverEventDate(startDateTime, timezone)
  const agreeError = errors?.agreeWaiver

  return (
    <>
      <Box display="flex" justifyContent="center">
        <img src={logo} alt="pga-hope" className={classes.logo} />
      </Box>
      <Typography
        variant="h4"
        component="h1"
        style={{ marginBottom: '32px', textAlign: 'center' }}
      >
        Participant Waiver
      </Typography>
      <Typography variant="body2" className={classes.paragraph}>
        In connection with my participation in {title} (the “Program”) to be
        held at <strong>{coachFacility?.name}</strong> (the “Host”) this{' '}
        <strong>{eventDate}</strong>. I agree and understand my presence and
        participation therein may expose me to known, unknown, anticipated, and
        unanticipated risks of harm or injury.
      </Typography>
      <ol className={classes.list}>
        <Typography variant="body2" component="li">
          I understand that the Released Parties (as defined below) will not
          maintain insurance which will cover me for either personal injury,
          property damage or medical expenses, and I accept full responsibility
          for the costs of treatment for any injury or damages suffered as a
          result of the conduct released herein while participating as a
          participant in connection with the Program.
        </Typography>
        <Typography variant="body2" component="li">
          <Typography variant="body2" className={classes.paragraph}>
            (a) In consideration of and as a prerequisite to my participation, I
            acknowledge that such risks exist and I, for myself and on behalf of
            my heirs, assigns, personal representatives and next of kin, Hereby
            Assume All Such Risks, and Release And Discharge Host, PGA
            Foundation, Inc. d/b/a PGA REACH, the Professional Golfers’
            Association of America and each of their respective officers,
            directors, agents, volunteers, members and employees (collectively,
            the “Released Parties”) from any and all liability, claims, demands
            or causes of action, including personal injury, disability, death or
            loss to person or property damage, whether or not occurring on the
            premises of Host or arising out of any condition of any premises
            owned by Host or used for the Program or the conduct of any person
            in connection with the preparation for, supervision of or conduct of
            the Program or any practice round or activity connected or related
            to it, whether caused by no fault or the active or inactive
            negligence of the Released Parties, but excluding gross negligence
            or intentional, willful or wanton acts.
          </Typography>
          <Typography variant="body2">
            (b) I FURTHER VOLUNTARILY ASSUME ALL RISKS, HAZARDS AND DANGERS
            arising from or relating in any way to the Program, whether
            occurring before, during, or after the Program, including, without
            limitation, the risk of contracting a communicable disease or
            illness. I hereby waive all claims and potential claims against the
            Released Parties relating to or arising from such risks, hazards and
            dangers.
          </Typography>
        </Typography>
        <Typography variant="body2" component="li">
          I grant full permission to each of the Released Parties, with no
          obligation to compensate me, to (i) use each of my name, voice,
          statements, image, likeness, and/or actions, in addition to any
          photographs, videotapes, motion pictures, recordings, and/or other
          records of performances(s) in the Program in perpetuity worldwide in
          any manner (as determined by the Released Parties) and in any and all
          media, whether now known or unknown or hereafter devised (the
          “Materials”) without any obligation to review or seek approval of a
          particular use and (ii) record on film, videotape, audio tape,
          electronically transmit or otherwise, my name, likeness, and
          performance in connection with PGA REACH. I represent that I have the
          right to grant such permissions.
        </Typography>
        <Typography variant="body2" component="li">
          The Released Parties are held expressly harmless by me, my heirs,
          successors, and assigns for any liability, legal or financial incurred
          as a result of said usage. I waive all rights to inspect or approve
          the product for its use.
        </Typography>
      </ol>
      <Typography variant="body2" className={classes.paragraph}>
        This release is being executed by Participant for good and valuable
        consideration exchanged between the parties, the sufficiency of which is
        hereby agreed to by the parties.
      </Typography>
      <Controller
        name="agreeWaiver"
        control={control}
        defaultValue={false}
        render={({ onChange, value, name }) => (
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name={name}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                inputProps={{ 'data-testid': 'agree-waiver' }}
              />
            }
            label="I have read and agree to the Participant Waiver *"
          />
        )}
      />
      {agreeError && (
        <FormHelperText error>{agreeError.message}</FormHelperText>
      )}
    </>
  )
}

Waiver.propTypes = {
  groupEvent: PropTypes.shape({
    title: PropTypes.string,
    startDateTime: PropTypes.string,
    timezone: PropTypes.string,
    coachFacility: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
}

export default Waiver
